import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';

const categories = [
  { title: 'Home & Furniture', desc: 'Furniture, storage, décor and essentials.' },
  { title: 'Kitchen & Dining', desc: 'Cookware, appliances, and kitchen products.' },
  { title: 'Home Improvement', desc: 'Tools, lighting, fittings, and hardware.' },
  { title: 'Décor & Lifestyle', desc: 'Interior and everyday lifestyle products.' },
  { title: 'Electronics & Appliances', desc: 'Selected electronics and home appliances.' },
  { title: 'Outdoor & Living', desc: 'Garden, outdoor and leisure products.' },
  { title: 'Business & Hospitality', desc: 'Products for commercial and institutional needs.' },
];

const supplySources = [
  { title: 'Global Commerce', desc: 'International commerce platforms.' },
  { title: 'B2B Sourcing', desc: 'Manufacturers, wholesalers, and sourcing networks.' },
  { title: 'Global Brands', desc: 'Established and independent brands.' },
  { title: 'Regional Supply', desc: 'African and local suppliers.' },
];

const benefits = [
  { title: 'Multiple Global Sources', desc: 'Access products across complementary supply corridors.' },
  { title: 'Local Market Relevance', desc: 'Global supply organized around African demand.' },
  { title: 'Simple Discovery', desc: 'Find products without navigating multiple sources independently.' },
  { title: 'Business Procurement', desc: 'Source products and suppliers beyond conventional retail shopping.' },
  { title: 'Connected Logistics', desc: 'Commerce supported by an expanding logistics infrastructure.' },
];

const steps = [
  { title: 'Discover', desc: 'Search or browse products.' },
  { title: 'Select', desc: 'Review product and purchasing information.' },
  { title: 'Shop or Source', desc: 'Purchase products or submit a business sourcing requirement.' },
  { title: 'Fulfil', desc: 'Access available delivery and fulfilment options.' },
];

const corridor = ['Global Supply Corridors', 'KambiHome', 'Kambi3PL', 'African Markets'];

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About KambiHome | Global Supply, Commerce & Procurement</title>
        <meta
          name="description"
          content="Learn how KambiHome connects global home and lifestyle supply with African consumers, businesses, and institutions through multiple supply corridors."
        />
        <meta
          name="keywords"
          content="KambiHome, global sourcing, commerce, procurement, African markets, home and lifestyle supply, supply corridors"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://kambihome.com/about" />
        <meta property="og:title" content="About KambiHome | Global Supply, Commerce & Procurement" />
        <meta
          property="og:description"
          content="Connecting global home and lifestyle supply with African demand through multiple global supply corridors."
        />
        <meta property="og:url" content="https://kambihome.com/about" />
      </Helmet>

      <Header />

      <main className="mt-16 text-gray-800 scroll-smooth">
        <section className="bg-green-50 py-20 px-4 text-center animate-fade-in">
          <div className="max-w-4xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-green-700 mb-3">
              About KambiHome
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-5 leading-tight">
              Connecting Global Supply with African Demand
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700">
              KambiHome is a global multi-corridor sourcing, commerce, and procurement infrastructure connecting international home and lifestyle supply with African demand.
            </p>
            <p className="mt-5 text-lg font-semibold text-gray-900">
              One platform. Multiple supply corridors. Greater access.
            </p>
          </div>
        </section>

        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-3">Explore Home & Lifestyle</h2>
            <p className="text-gray-600 mb-10 max-w-2xl mx-auto">
              Discover products organized around everyday living, commercial needs, and institutional demand.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {categories.map((item) => (
                <div
                  key={item.title}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 h-full"
                >
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
            <a
              href="/products"
              className="cursor-pointer inline-block mt-9 bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 hover:scale-105 transition-all duration-300 shadow-sm"
            >
              Explore Categories
            </a>
          </div>
        </section>

        <section className="bg-gray-50 py-16 px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
              <p className="text-sm font-semibold text-green-700 mb-2">For Consumers</p>
              <h2 className="text-2xl font-bold mb-3">Discover & Shop Globally</h2>
              <p className="text-gray-600 leading-relaxed flex-1">
                Explore home and lifestyle products from connected global supply corridors through one simple KambiHome experience.
              </p>
              <a
                href="/products"
                className="cursor-pointer mt-6 inline-flex w-fit bg-green-600 text-white px-5 py-3 rounded-md font-semibold hover:bg-green-700 hover:scale-105 transition-all duration-300"
              >
                Start Shopping
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
              <p className="text-sm font-semibold text-green-700 mb-2">For Businesses</p>
              <h2 className="text-2xl font-bold mb-3">Source Globally</h2>
              <p className="text-gray-600 leading-relaxed">
                KambiHome connects businesses to international products, suppliers, and wholesale opportunities.
              </p>
              <p className="mt-4 text-sm font-semibold text-gray-700 flex-1">
                Retailers • SMEs • Hospitality • Interiors • Property • Commercial Buyers
              </p>
              <a
                href="/contact"
                className="cursor-pointer mt-6 inline-flex w-fit bg-green-600 text-white px-5 py-3 rounded-md font-semibold hover:bg-green-700 hover:scale-105 transition-all duration-300"
              >
                Source for Business
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-green-700 mb-3">
              Our Global Supply Network
            </p>
            <h2 className="text-3xl font-bold mb-3">Multiple Corridors. One KambiHome.</h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-10">
              KambiHome connects complementary global and regional supply sources through one platform.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              {supplySources.map((item) => (
                <div
                  key={item.title}
                  className="bg-green-50 p-6 rounded-xl border border-green-100 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 h-full"
                >
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
{/* 
            <p className="mt-8 text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Our existing relationship with <strong>AliExpress</strong> is one established supply corridor within the wider KambiHome network.
            </p> */}
          </div>
        </section>

        <section className="bg-gray-100 py-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-10">How It Works</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              {steps.map((step, idx) => (
                <div
                  key={step.title}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 h-full"
                >
                  <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold text-sm mb-4">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-green-700 mb-3">
              Connected Logistics
            </p>
            <h2 className="text-3xl font-bold mb-3">Global Supply. Connected Delivery.</h2>
            <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
              KambiHome connects with Kambi3PL for logistics, warehousing, and fulfilment capabilities across the TCHIKAMALOR Global ecosystem.
            </p>

            <div className="mt-10 flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 md:gap-4">
              {corridor.map((item, index) => (
                <React.Fragment key={item}>
                  <div className="flex-1 min-h-[88px] bg-green-50 border border-green-100 rounded-xl shadow-sm px-5 py-5 flex items-center justify-center font-semibold text-gray-800 hover:shadow-lg hover:scale-105 transition-all duration-300">
                    {item}
                  </div>
                  {index < corridor.length - 1 && (
                    <span className="text-green-600 text-2xl font-bold rotate-90 md:rotate-0" aria-hidden="true">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-green-50 py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">Why KambiHome?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((item) => (
                <div
                  key={item.title}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 h-full"
                >
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-5">Our Mission</h2>
            <p className="text-lg leading-relaxed text-gray-700">
              To make global home and lifestyle commerce and procurement more accessible, connected, and relevant to African markets.
            </p>
          </div>
        </section>

        <section className="bg-green-700 text-white py-16 px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Connect to Global Supply</h2>
            <p className="mb-8 text-green-50 text-lg">Discover products. Shop globally. Source for business.</p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">
              <a
                href="/products"
                className="cursor-pointer inline-block bg-white text-green-700 px-6 py-3 rounded-md font-semibold hover:bg-green-50 hover:scale-105 transition-all duration-300 shadow"
              >
                Explore Products
              </a>
              <a
                href="/contact"
                className="cursor-pointer inline-block border border-white text-white px-6 py-3 rounded-md font-semibold hover:bg-white hover:text-green-700 hover:scale-105 transition-all duration-300"
              >
                Source for Business
              </a>
              <a
                href="/contact"
                className="cursor-pointer inline-block border border-green-200 text-white px-6 py-3 rounded-md font-semibold hover:bg-white hover:text-green-700 hover:scale-105 transition-all duration-300"
              >
                Become a Supply Partner
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutPage;
